// Atomowy wiersz mini-tabeli progów cenowych — kontrolowane inputy qtyFrom + price,
// kosz do usunięcia, badge "aktualny próg". Brak własnej logiki PUT — wszystko przez parenta.

import React, { type FC } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { IconButton, Tooltip } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import { IPriceThreshold } from 'api/endpoints/useGetSalesCrmOfferPositions';

import styles from 'theme/components/containers/TraderClientOfferProductsContainer/PriceThresholdsEditor.module.scss';

export interface IPriceThresholdRowProps {
  qtyFrom: number;
  price: number;
  qtyFromError?: string;
  priceError?: string;
  /** Czy ten próg jest efektywny dla aktualnej `positionQty`. */
  isActive?: boolean;
  /** Ilość pozycji — używana w tooltipie badge'a "aktualny próg". */
  positionQty: number;
  /** Jednostka miary produktu — używana jako suffix przy ilości i w tooltipie. */
  positionUnit: string;
  /** Wyłącza inputy + ukrywa kosz (pending PUT albo readOnly z parenta). */
  disabled?: boolean;
  /** Pełny readOnly (historyczna wersja) — kosz hidden, nie tylko disabled. */
  readOnly?: boolean;
  /**
   * Tryb wariantowy. Jeśli true, prefix wiersza to "biorę" (klient wybiera tę parę),
   * jeśli false — "od" (klasyczny próg ilościowy od podanej ilości w górę).
   */
  asVariants?: boolean;
  onChange: (patch: Partial<IPriceThreshold>) => void;
  onRemove: () => void;
}

const PriceThresholdRow: FC<IPriceThresholdRowProps> = ({
  qtyFrom,
  price,
  qtyFromError,
  priceError,
  isActive = false,
  positionQty,
  positionUnit,
  disabled = false,
  readOnly = false,
  asVariants = false,
  onChange,
  onRemove
}) => {
  const { t } = useTranslation();

  const handleQtyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value;
    // Pusta wartość = 0 (Yup wykryje jako !positive).
    const parsed = raw === '' ? 0 : parseInt(raw, 10);
    onChange({ qtyFrom: Number.isNaN(parsed) ? 0 : parsed });
  };

  const handlePriceChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value;
    const parsed = raw === '' ? 0 : parseFloat(raw);
    onChange({ price: Number.isNaN(parsed) ? 0 : parsed });
  };

  // Blokuje znaki niedopuszczalne dla liczby całkowitej w `qtyFrom`.
  // Pomija nawigację (Backspace, Arrow*, Tab, Home, End, Delete) i kombinacje z modyfikatorami.
  const handleQtyKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.ctrlKey || event.metaKey || event.altKey) return;
    if (event.key.length > 1) return; // klawisze typu Arrow*, Backspace, Tab itd.
    if (!/^[0-9]$/.test(event.key)) {
      event.preventDefault();
    }
  };

  // Dla ceny dopuszczamy też kropkę (separator dziesiętny w HTML number input).
  // Przecinek jest blokowany — HTML number nie rozumie przecinka w PL locale.
  const handlePriceKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.ctrlKey || event.metaKey || event.altKey) return;
    if (event.key.length > 1) return;
    if (event.key === '.') {
      // Dopuść tylko jedną kropkę.
      if ((event.currentTarget.value ?? '').includes('.')) {
        event.preventDefault();
      }
      return;
    }
    if (!/^[0-9]$/.test(event.key)) {
      event.preventDefault();
    }
  };

  // Sanityzuje pastę: zostawia tylko cyfry (qty) lub cyfry + pierwszą kropkę (price).
  const sanitizePastedQty = (text: string): string => text.replace(/[^0-9]/g, '');

  const sanitizePastedPrice = (text: string): string => {
    const cleaned = text.replace(/,/g, '.').replace(/[^0-9.]/g, '');
    const firstDot = cleaned.indexOf('.');
    if (firstDot === -1) return cleaned;
    return cleaned.slice(0, firstDot + 1) + cleaned.slice(firstDot + 1).replace(/\./g, '');
  };

  const handleQtyPaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const sanitized = sanitizePastedQty(event.clipboardData.getData('text'));
    if (sanitized === '') return;
    const parsed = parseInt(sanitized, 10);
    onChange({ qtyFrom: Number.isNaN(parsed) ? 0 : parsed });
  };

  const handlePricePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const sanitized = sanitizePastedPrice(event.clipboardData.getData('text'));
    if (sanitized === '' || sanitized === '.') return;
    const parsed = parseFloat(sanitized);
    onChange({ price: Number.isNaN(parsed) ? 0 : parsed });
  };

  return (
    <div className={classnames(styles.thresholdRow, { [styles.activeRow]: isActive })}>
      <div
        className={styles.qtyCell}
        data-mobile-label={asVariants ? t('Ilość') : t('Od ilości')}>
        <span className={styles.cellPrefix}>{asVariants ? t('biorę') : t('od')}</span>
        <input
          type="number"
          inputMode="numeric"
          className={classnames(styles.qtyInput, { [styles.hasError]: !!qtyFromError })}
          value={qtyFrom > 0 ? qtyFrom : ''}
          min={1}
          step={1}
          disabled={disabled || readOnly}
          onChange={handleQtyChange}
          onKeyDown={handleQtyKeyDown}
          onPaste={handleQtyPaste}
          onWheel={(e) => e.currentTarget.blur()}
          aria-label={t('Od ilości')}
        />
        <span className={styles.cellSuffix}>{positionUnit}</span>
      </div>

      <div
        className={styles.priceCell}
        data-mobile-label={asVariants ? t('Cena całkowita (netto)') : t('Cena za sztukę (netto)')}>
        <span className={styles.cellPrefix}>{t('za')}</span>
        <input
          type="number"
          inputMode="decimal"
          className={classnames(styles.priceInput, { [styles.hasError]: !!priceError })}
          value={price > 0 ? price : ''}
          min={0.01}
          step={0.01}
          disabled={disabled || readOnly}
          onChange={handlePriceChange}
          onKeyDown={handlePriceKeyDown}
          onPaste={handlePricePaste}
          onWheel={(e) => e.currentTarget.blur()}
          aria-label={asVariants ? t('Cena całkowita (netto)') : t('Cena za sztukę (netto)')}
        />
        <span className={styles.cellSuffix}>
          {asVariants ? t('zł łącznie') : t('zł/{{unit}}', { unit: positionUnit })}
        </span>
      </div>

      <div className={styles.badgeCell}>
        {isActive && !asVariants && (
          <Tooltip
            title={t('Aktualny próg dla ilości {{qty}} {{unit}}', {
              qty: positionQty,
              unit: positionUnit
            })}
            arrow
            placement="top">
            <span className={styles.activeBadge}>{t('Aktualny')}</span>
          </Tooltip>
        )}
      </div>

      <div className={styles.actionsCell}>
        {!readOnly && (
          <IconButton
            size="small"
            className={styles.removeBtn}
            onClick={onRemove}
            disabled={disabled}
            aria-label={t('Usuń próg')}>
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        )}
      </div>

      {(qtyFromError || priceError) && (
        <div className={styles.rowErrors}>
          {qtyFromError && <div className={styles.errorText}>{t(qtyFromError)}</div>}
          {priceError && <div className={styles.errorText}>{t(priceError)}</div>}
        </div>
      )}
    </div>
  );
};

export default PriceThresholdRow;
